/**
 * The cpanel admin WebSocket wire protocol, as pure functions (cpanel
 * `hooks/useWebSocket.ts` + the `WebSocketProvider` dispatch in its router).
 * The same four-frame handshake the merchant panel's legacy socket speaks:
 *
 *   server → `{"type":"waiting_auth"}`   prove who you are
 *   client → `{"type":"auth", content}`  the access token, verbatim
 *   server → `{"type":"ack_message"}`    authenticated; pushes start
 *   server → `{"type":"auth_failed"}`    rejected (also `error_message`)
 *
 * After `ack_message` this app acts on `refresh` ("the orders list changed", no
 * payload) and on the four chat pushes the support inbox lives on:
 *
 *   `chat_message`            a new message (content = the message)
 *   `new_chat`                a chat was opened / handed over (content = the chat)
 *   `new_chat_status`         a chat changed status
 *   `messages_has_been_seen`  the other party read our messages
 *
 * `subscribe_to_offer` feeds the live order map.
 */

/** The loosely-typed `content` of a chat push; the consumer validates it. */
export type AdminSocketContent = Record<string, unknown>;

export type AdminSocketAction =
  | { kind: 'authenticate' }
  | { kind: 'authenticated' }
  | { kind: 'auth-failed' }
  | { kind: 'refresh' }
  | { kind: 'courier-offer'; content: AdminSocketContent }
  | { kind: 'chat-message'; content: AdminSocketContent }
  | { kind: 'new-chat'; content: AdminSocketContent }
  | { kind: 'chat-status' }
  | { kind: 'messages-seen' }
  | { kind: 'ignore' };

/** The actions a chat consumer (`onChatEvent`) receives. */
export type AdminSocketChatAction = Extract<
  AdminSocketAction,
  { kind: 'chat-message' | 'new-chat' | 'chat-status' | 'messages-seen' }
>;

/** The keepalive answer arrives as a bare string, not JSON. */
const PONG_FRAME = 'pong';

interface Frame {
  type: unknown;
  content: AdminSocketContent;
}

function readFrame(raw: string): Frame | undefined {
  try {
    const message: unknown = JSON.parse(raw);
    if (!message || typeof message !== 'object' || !('type' in message)) return undefined;
    const content =
      'content' in message && message.content && typeof message.content === 'object'
        ? (message.content as AdminSocketContent)
        : {};
    return { type: message.type, content };
  } catch {
    return undefined;
  }
}

/** Parse a raw frame into an action. Never throws on a malformed frame. */
export function readAdminSocketFrame(raw: string): AdminSocketAction {
  if (raw === PONG_FRAME) return { kind: 'ignore' };
  const frame = readFrame(raw);
  switch (frame?.type) {
    case 'waiting_auth':
      return { kind: 'authenticate' };
    case 'ack_message':
      return { kind: 'authenticated' };
    case 'auth_failed':
    case 'error_message':
      return { kind: 'auth-failed' };
    case 'subscribe_to_offer':
      return { kind: 'courier-offer', content: frame?.content ?? {} };
    case 'refresh':
      return { kind: 'refresh' };
    case 'chat_message':
      return { kind: 'chat-message', content: frame?.content ?? {} };
    case 'new_chat':
      return { kind: 'new-chat', content: frame?.content ?? {} };
    case 'new_chat_status':
      return { kind: 'chat-status' };
    case 'messages_has_been_seen':
      return { kind: 'messages-seen' };
    default:
      return { kind: 'ignore' };
  }
}

/** The authentication frame, sent in reply to `waiting_auth`. */
export function adminAuthFrame(token: string): string {
  return JSON.stringify({ type: 'auth', content: token });
}

/** The keepalive frame (cpanel sends it every 10 s once authenticated). */
export function adminPingFrame(): string {
  return JSON.stringify({ type: 'PING' });
}
